import type { NotFoundSubject } from '@schemas/error-pages'
import { NOT_FOUND_LAYOUT_BY_REASON, NotFoundLayoutEnum, NotFoundReasonEnum } from '@schemas/error-pages'
import { useEffect } from 'react'
import { AccessRevoked } from './not-found-access-revoked.tsx'
import { LedgerLayout } from './not-found-ledger.tsx'
import { StatementLayout } from './not-found-statement.tsx'
import { SwitchboardLayout } from './not-found-switchboard.tsx'

export interface NotFoundPageProps {
  /** Not narrowed to NotFoundReason: real call sites include a route/query param, which is
   * untrusted input until validated below. */
  reason: string
  subject?: NotFoundSubject
}

// RAL-162/163: the 404 route resolves a reason code and picks one of four layouts. No code
// falls through to a default (rule 6) — an unresolved reason is a bug, logged here and rendered
// as `statement`, but never silently guessed at.
export function NotFoundPage({ reason: rawReason, subject }: NotFoundPageProps) {
  const parsed = NotFoundReasonEnum.safeParse(rawReason)

  useEffect(() => {
    if (!parsed.success) console.error(`NotFoundPage: unresolved reason code "${rawReason}"`)
  }, [parsed.success, rawReason])

  if (!parsed.success) return <StatementLayout />

  const reason = parsed.data
  const layout = NOT_FOUND_LAYOUT_BY_REASON[reason]

  if (layout === NotFoundLayoutEnum.enum.refusal) return <AccessRevoked />

  if (layout === NotFoundLayoutEnum.enum.ledger) {
    if (subject?.layout === NotFoundLayoutEnum.enum.ledger) return <LedgerLayout reason={reason} subject={subject} />
    // Ledger needs the deleted object's surviving metadata; without it, degrade rather than
    // render a survivors block with blanks (RAL-163).
    return <StatementLayout />
  }

  if (layout === NotFoundLayoutEnum.enum.switchboard) {
    if (subject?.layout === NotFoundLayoutEnum.enum.switchboard) {
      // The correct answer for an unreachable target is access_revoked, never a switch row
      // that would fail (RAL-163) — the caller must have verified reachability already.
      return subject.target.reachable ? <SwitchboardLayout reason={reason} subject={subject} /> : <AccessRevoked />
    }
    return <StatementLayout />
  }

  return <StatementLayout />
}
